import {defineStore} from "pinia";
import JSONAPISerializer from "json-api-serializer";
import type {Options} from "json-api-serializer";

import type {JSONAPIDocument} from "json-api-serializer";
import type {AxiosRequestConfig, CancelTokenSource} from "axios";
import axios from "axios";
import type {AxiosError} from "axios";
import {ref} from "vue";
import {ApiErrorResponse} from "@/types/object_classes/ApiErrorResponse";
import type {QueryFilter} from "@/types/object_classes/QueryFilter";
import pluralize from "pluralize";
import {showError} from "@/services/ShowMessages";
import type {JsonApiPagination} from "@/types/object_classes/JsonApiPagination";
import {JsonApiErrors} from "@/helpers/JsonApiErrors";
import type {ApiCallOptions} from "@/types/object_classes/ApiCallOptions";
import {useAuthStore} from "@/stores/auth";
import {activeFlowId, activeFlowName, activeFlowStep, adoptSeq, describeError, nextRequestSeq, normalizeAttributes} from "@/services/observability/FlowLogger";
import * as Sentry from "@sentry/vue";


export const useApiStore = defineStore("api", () => {
    const authStore = useAuthStore();
    const BASE_URL = import.meta.env.VITE_BASE_URL;
    const PARALLEL_REQUESTS = import.meta.env.VITE_PARALLEL_REQUESTS;

    const CancelToken = ref(axios.CancelToken);

    const configApi: AxiosRequestConfig = {
        baseURL: `${BASE_URL}/api/v1`,
        headers: {
            "Content-Type": "application/vnd.api+json",
            "Accept": "application/vnd.api+json"
        }
    };

    const axiosApi = ref(axios.create(configApi));

    const configAuth: AxiosRequestConfig = {
        baseURL: `${BASE_URL}/api`,
        headers: {
            "Content-Type": "application/vnd.api+json",
            "Accept": "application/vnd.api+json"
        }
    };

    const axiosAuth = ref(axios.create(configAuth));

    const reportRequestFailure = (error: any): void => {
        if (error?.code === "ERR_CANCELED") {
            return;
        }

        Sentry.logger.error("api.request_failed", normalizeAttributes(describeError(error)));

        if (error?.response === undefined) {
            Sentry.captureException(error);
        }
    };

    const attachFlowHeaders = (config: any): any => {
        const flowId = activeFlowId();

        if (flowId !== null) {
            config.headers["X-Sentry-Flow-Id"] = flowId;
            config.headers["X-Sentry-Flow-Name"] = activeFlowName();

            const step = activeFlowStep();
            if (step !== null) {
                config.headers["X-Sentry-Flow-Step"] = step;
            }

            config.headers["X-Sentry-Flow-Seq"] = String(nextRequestSeq());
        }

        return config;
    };

    const adoptFlowSeqFromHeaders = (headers: any): void => {
        const value = headers?.["x-sentry-flow-seq"];
        if (typeof value === "string") {
            adoptSeq(Number(value));
        }
    };

    axiosApi.value.interceptors.request.use(attachFlowHeaders);
    axiosAuth.value.interceptors.request.use(attachFlowHeaders);

    axiosApi.value.interceptors.response.use((response) => {
        adoptFlowSeqFromHeaders(response.headers);
        return response;
    }, (error) => {
        adoptFlowSeqFromHeaders(error?.response?.headers);
        reportRequestFailure(error);
        return Promise.reject(error);
    });

    axiosAuth.value.interceptors.response.use((response) => {
        adoptFlowSeqFromHeaders(response.headers);
        return response;
    }, (error) => {
        adoptFlowSeqFromHeaders(error?.response?.headers);
        reportRequestFailure(error);
        return Promise.reject(error);
    });

    const Serializer = new JSONAPISerializer();

    const meta = ref<Object | undefined>();

    const json_api_types = [
        {
            type: "client-documentation-titles", schema: {
                relationships: {
                    parent: {type: "client-documentation-titles"},
                    children: {type: "client-documentation-titles"},
                    subtitles: {type: "client-documentation-subtitles"},
                    texts: {type: "client-documentation-texts"},
                    images: {type: "client-documentation-images"},
                }
            }
        },
        {
            type: "client-documentation-subtitles", schema: {
                relationships: {
                    title: {type: "client-documentation-titles"},
                    texts: {type: "client-documentation-texts"},
                    images: {type: "client-documentation-images"},
                }
            }
        },
        {type: "client-documentation-texts", schema: {}},
        {type: "client-documentation-images", schema: {}},
        {
            type: "cleo-conversations", schema: {
                relationships: {
                    messages: {type: "cleo-conversation-messages"},
                }
            }
        },
        {
            type: "cleo-conversation-messages", schema: {
                relationships: {
                    conversation: {type: "cleo-conversations"},
                    tools: {type: "cleo-conversation-message-tools"},
                }
            }
        },
        {
            type: "cleo-conversation-message-tools", schema: {
                relationships: {
                    message: {type: "cleo-conversation-messages"},
                }
            }
        },
        {
            type: "users", schema: {
                blacklist: ["repeat_password"],
                relationships: {
                    roles: {
                        type: "roles"
                    },
                    userSettings: {
                        type: "user-settings"
                    },
                }
            }
        },
        {
            type: "domestic-shipments", schema: {
                relationships: {
                    domesticPod: {
                        type: "dommestic-pods"
                    },
                    invoiceLines: {
                        type: "invoice-lines"
                    },
                    shipmentGoods: {
                        type: "domestic-road-shipment-goods"
                    },
                    shipmentActivities: {
                        type: "domestic-road-shipment-activities"
                    },
                    orderData: {
                        type: "order-datas"
                    },
                    transportOrder: {
                        type: "domestic-road-transport-orders"
                    },
                    domesticRoadShipmentTrackingUrl: {
                        type: "domestic-road-shipment-tracking-urls"
                    },
                    company: {
                        type: "companies"
                    },
                    companyContracts: {
                        type: "company-contracts"
                    },
                    apiStatus: {
                        type: "api-statuses"
                    },

                }
            }
        },
        {
            type: "international-shipments", schema: {
                relationships: {
                    internationalPod: {
                        type: "international-pods"
                    },
                    invoiceLines: {
                        type: "invoice-lines"
                    },
                    shipmentGoods: {
                        type: "international-road-shipment-goods"
                    },
                    shipmentActivities: {
                        type: "international-road-shipment-activities"
                    },
                    orderData: {
                        type: "order-datas"
                    },
                    transportOrder: {
                        type: "international-road-transport-orders"
                    },
                    internationalRoadShipmentTrackingUrl: {
                        type: "international-road-shipment-tracking-urls"
                    },
                    apiStatus: {
                        type: "api-statuses"
                    },

                }
            }
        },
        {
            type: 'charts', schema: {
                relationships: {
                    chartDashboards: {
                        type: "chart-dashboards"
                    },
                }
            }
        },
        {
            type: 'chart-dashboards', schema: {
                relationships: {
                    charts: {
                        type: "charts"
                    },
                }
            }
        },
        {type: 'chart-chart-dashboards', schema: {}},
        {
            type: "tms-sea-shipments", schema: {
                relationships: {
                    seaInvoiceShipments: {
                        type: "sea-invoice-shipments"
                    },
                    seaShipmentContainers: {
                        type: "sea-shipment-containers"
                    },
                    apiStatus: {
                        type: "api-statuses"
                    },
                }
            }
        },
        {
            type: "sea-shipment-containers", schema: {
                relationships: {
                    routings: {
                        type: "routings"
                    },
                    freightSellEsts: {
                        type: "freight-sell-ests"
                    },
                    seaAirDocuments: {
                        type: "sea-air-documents"
                    },
                    seaInvoiceShipments: {
                        type: "sea-invoice-shipments"
                    },
                    apiStatus: {
                        type: "api-statuses"
                    },
                }
            }
        },
        {
            type: "tms-air-shipments", schema: {
                relationships: {
                    routings: {
                        type: "routings"
                    },
                    seaAirDocuments: {
                        type: "sea-air-documents"
                    },
                    airInvoiceShipments: {
                        type: "sea-invoice-shipments"
                    },
                    apiStatus: {
                        type: "api-statuses"
                    },
                }
            }
        },
        {type: "unregistered-clients", schema: {}},
        {type: "routings", schema: {}},
        {type: "freight-sell-ests", schema: {}},
        {type: "sea-air-documents", schema: {}},
        {
            type: "outbounds", schema: {
                relationships: {
                    carrier: {
                        type: "shippers"
                    },
                    apiStatus: {
                        type: "api-statuses"
                    },
                }
            }
        },
        {
            type: "inbounds", schema: {
                relationships: {
                    apiStatus: {
                        type: "api-statuses"
                    },
                },
            }
        },
        {type: "bussiness-lines", schema: {}},
        {type: 'table-column-changes', schema: {}},
        {
            type: "client-permission-pages", schema: {
                relationships: {
                    forms: {
                        type: "forms"
                    },
                    listings: {
                        type: "listings"
                    },
                    actions: {
                        type: "actions"
                    },
                    pageFilters: {
                        type: "page-filters"
                    }
                }
            }
        },
        {type: "client-permissions", schema: {}},
        // {
        //     type: 'client-roles', schema: {
        //         relationships: {
        //             clientPermissions: {
        //                 type: "client-permissions"
        //             },
        //         }
        //     }
        // },
        {
            type: "klg-permission-pages", schema: {
                relationships: {
                    forms: {
                        type: "forms"
                    },
                    listings: {
                        type: "listings"
                    },
                    actions: {
                        type: "actions"
                    },
                    pageFilters: {
                        type: "page-filters"
                    }
                }
            }
        },
        {
            type: "roles", schema: {
                relationships: {
                    permissions: {
                        type: "permissions"
                    }
                }
            }
        },
        {
            type: "forms", schema: {
                relationships: {
                    klgPermissionPages: {
                        type: "klg-permission-pages"
                    },
                    formFields: {
                        type: "form-fields"
                    },
                    clientPermissionPages: {
                        type: "client-permission-pages"
                    }
                }

            }
        },
        {type: "form-fields", schema: {}},
        {
            type: "listings", schema: {
                relationships: {
                    klgPermissionPages: {
                        type: "klg-permission-pages"
                    },
                    clientPermissionPages: {
                        type: "client-permission-pages"
                    },
                    forms: {
                        type: "forms"
                    },
                    listingFilters: {
                        type: "listing-filters"
                    },
                    listingActions: {
                        type: "listing-actions"
                    }
                }
            }
        },
        {type: "page-filters", schema: {}},
        {type: "listing-filters", schema: {}},
        {type: "listing-actions", schema: {}},
        {type: "actions", schema: {}},
        {type: "client-users", schema: {}},
        {type: "client-user-permissions", schema: {}},
        {type: "permissions", schema: {}},
        {type: "user-tokens", schema: {}},
        {
            type: "user-impersonate-tokens", schema: {
                blacklist: ["id", "token", "code"]
            }
        },
        {
            type: "countries", schema: {
                cities: {
                    type: "cities"
                }
            }
        },
        {type: "counties", schema: {}},
        {type: "localities", schema: {}},
        {type: "cities", schema: {}},
        {type: "activity-logs", schema: {}},
        {
            type: "companies", schema: {
                relationships: {
                    bussinessLines: {
                        type: "bussiness-lines"
                    },
                    optionValues: {
                        type: "option-values"
                    },
                    companyBusinessLines: {
                        type: "company-business-lines"
                    },
                    paymentTerm: {
                        type: "payment-terms"
                    },
                    creditCurrency: {
                        type: "option-values"
                    },
                    salesPerson: {
                        type: "klg-users"
                    },
                    internationalRoadOrderEntry: {
                        type: "international-road-order-entries"
                    },
                    domesticRoadOrderEntry: {
                        type: "domestic-road-order-entries"
                    },
                    strongoOrderEntry: {
                        type: "strongo-order-entries"
                    },
                    orderEntryLeadtimes: {
                        type: "order-entry-leadtimes"
                    },
                    strongoLeadtimes: {
                        type: "strongo-order-entry-leadtimes"
                    },
                    orderEntryAddresses: {
                        type: "order-entry-addresses"
                    },
                    strongoOrderEntryAddresses: {
                        type: "strongo-order-entry-addresses"
                    },
                    orderEntryGoods: {
                        type: "order-entry-goods"
                    },
                    companyStrongoSetting: {
                        type: "company-strongo-settings"
                    }
                }
            }
        },
        { type: 'company-strongo-settings', schema: {}},
        {
            type: 'company-business-lines', schema: {
                relationships: {
                    companyEmail: {
                        type: 'company-emails'
                    },
                    podEmail: {
                        type: 'company-emails'
                    },
                    companyKpis: {
                        type: 'company-kpis'
                    },
                    companyAnnexes: {
                        type: 'company-annexes'
                    },
                },
            }
        },
        {type: 'company-emails', schema: {}},
        {
            type: "app-statuses", schema: {
                relationships: {
                    apiStatuses: {
                        type: "api-statuses"
                    }
                }
            }
        },
        {type: "api-statuses", schema: {
                relationships: {
                    appStatuses: {
                        type: "app-statuses"
                    }
                }
            }},
        {
            type: "vbs-report-outbounds", schema: {
                relationships: {
                    carrier: {
                        type: "shippers"
                    },
                    apiStatus: {
                        type: "api-statuses"
                    },
                }
            }
        },
        {type: "vbs-report-inbounds", schema: {
                relationships: {
                    apiStatus: {
                        type: "api-statuses"
                    },
                }
            }},
        {type: "vbs-upload-file-logs", schema: {}},
        {type: "api-import-results", schema: {}},
        {
            type: "tooltip-categories", schema: {
                relationships: {
                    tooltipValues: {
                        type: "tooltip-values"
                    }
                }
            }
        },
        {type: "tooltip-values", schema: {}},
        {
            type: "customer-ledger-entries", schema: {
                relationships: {
                    customerLedgerGlEntries: {
                        type: "customer-ledger-gl-entries"
                    },
                    invoice: {
                        type: "invoices"
                    },
                    seaInvoice: {
                        type: "sea-invoices"
                    },
                    strongoInvoices: {
                        type: "strongo-invoices"
                    },
                }
            }
        },
        {type: "customer-ledger-gl-entries", schema: {}},
        {type: "vendor-ledger-entries", schema: {}},
        {type: "customers", schema: {}},
        {type: "vendors", schema: {}},
        {type: "customer-posting-groups", schema: {}},
        {type: "vendor-posting-groups", schema: {}},
        {type: "reminder-terms", schema: {}},
        {type: "post-codes", schema: {}},
        {type: "customer-bank-accounts", schema: {}},
        {type: "payment-terms", schema: {}},
        {type: "currencies", schema: {}},
        {type: "invoices", schema: {}},
        {
            type: "invoice-lines", schema: {
                relationships: {
                    domesticShipment: {
                        type: "domestic-shipments"
                    },
                    internationalShipment: {
                        type: "international-shipments"
                    }
                }
            }
        },
        {type: "domestic-pods", schema: {}},
        {type: "international-pods", schema: {}},
        {type: "erp-invoice-line-names", schema: {}},
        {type: "option-values", schema: {}},
        {
            type: "sea-invoices", schema: {
                seaInvoiceShipments: {
                    users: {
                        type: "sea-invoice-shipments"
                    }
                },
                airInvoiceShipments: {
                    users: {
                        type: "sea-invoice-shipments"
                    }
                },
                customerLedgerEntry: {
                    users: {
                        type: "customer-ledger-entries"
                    }
                }
            }
        },
        {
            type: "sea-invoice-shipments", schema: {
                tmsSeaShipment: {
                    users: {
                        type: "tms-sea-shipments"
                    }
                },
                airSeaShipment: {
                    users: {
                        type: "tms-air-shipments"
                    }
                },
                seaInvoice: {
                    users: {
                        type: "sea-invoices"
                    }
                }
            }
        },
        {
            type: "international-road-order-entries", schema: {
                relationships: {
                    incoterms: {
                        type: "incoterms"
                    },
                    packages: {
                        type: "packages"
                    },
                    orderEntryFeatures: {
                        type: "order-entry-features"
                    }
                }
            }
        },
        {
            type: "domestic-road-order-entries", schema: {
                relationships: {
                    packages: {
                        type: "packages"
                    },
                    orderEntryFeatures: {
                        type: "order-entry-features"
                    }
                }
            }
        },
        {
            type: "strongo-order-entries", schema: {
                relationships: {
                    packages: {
                        type: "packages"
                    },
                    orderEntryFeatures: {
                        type: "order-entry-features"
                    }
                }
            }
        },
        {type: "incoterms", schema: {}},
        {type: "packages", schema: {}},
        {type: "order-entry-features", schema: {}},
        {type: "order-entry-leadtimes", schema: {}},
        {type: "strongo-order-entry-leadtimes", schema: {}},
        {type: "strongo-departments", schema: {}},
        {
            type: "order-entry-addresses", schema:
                {
                    blacklist: ["display_name"],
                    relationships: {
                        contacts: {
                            type: "order-entry-contacts",
                        },
                        features: {
                            type: "order-entry-features",
                        },
                    }
                }
        },
        {
            type: "order-entry-contacts", schema:
                {
                    blacklist: ["display_name"]
                }
        },
        {
            type: "strongo-order-entry-addresses", schema:
                {
                    relationships: {
                        contacts: {
                            type: "strongo-order-entry-contacts",
                        },
                    }
                }
        },
        {
            type: "strongo-order-entry-contacts", schema: {}
        },
        {type: "order-entry-address-order-entry-features", schema: {}},
        {type: "order-entry-goods", schema: {}},
        {
            type: "domestic-road-shipment-goods", schema: {
                relationships: {
                    adrs: {
                        type: "domestic-road-shipment-good-adrs",
                    },
                }
            }
        },
        {type: "domestic-road-shipment-good-adrs", schema: {}},
        {type: "domestic-road-shipment-activities", schema: {}},
        {
            type: "domestic-road-transport-orders", schema: {
                // blacklist: ['loading_features', 'unloading_features'],
                relationships: {
                    goods: {
                        type: "domestic-road-transport-order-goods",
                    },
                }
            }
        },
        {
            type: "domestic-road-transport-order-goods", schema: {
                relationships: {
                    adrs: {
                        type: "domestic-road-transport-order-good-adrs",
                    },
                }
            }
        },
        {type: "domestic-road-transport-order-good-adrs", schema: {}},
        {type: "domestic-road-transport-order-order-entry-features", schema: {}},
        {
            type: "international-road-transport-orders", schema: {
                // blacklist: ['loading_features', 'unloading_features'],
                relationships: {
                    goods: {
                        type: "international-road-transport-order-goods",
                    },
                }
            }
        },
        {
            type: "international-road-transport-order-goods", schema: {
                relationships: {
                    adrs: {
                        type: "international-road-transport-order-good-adrs",
                    },
                }
            }
        },
        {type: "international-road-transport-order-good-adrs", schema: {}},
        {
            type: "international-road-shipment-goods", schema: {
                relationships: {
                    adrs: {
                        type: "international-road-shipment-good-adrs",
                    },
                }
            }
        },
        {type: "international-road-transport-order-order-entry-features", schema: {}},
        {type: "international-road-shipment-good-adrs", schema: {}},
        {type: "international-road-shipment-activities", schema: {}},
        {type: "domestic-road-shipment-tracking-urls", schema: {}},
        {type: "track-trace-domestic-shipments", schema: {
                relationships: {
                    apiStatus: {
                        type: "api-statuses",
                    },
                }
            }
        },
        {
            type: "track-trace-domestic-road-shipment-tracking-urls", schema: {
                relationships: {
                    shipment: {
                        type: "track-trace-domestic-shipments",
                    },
                }
            }
        },
        {type: "international-road-shipment-tracking-urls", schema: {}},
        {type: "track-trace-international-shipments", schema: {
                relationships: {
                    apiStatus: {
                        type: "api-statuses",
                    },
                }
            }
        },
        {
            type: "track-trace-international-road-shipment-tracking-urls", schema: {
                relationships: {
                    shipment: {
                        type: "track-trace-international-shipments",
                    },
                }
            }
        },

        {type: "strongo-transport-orders", schema: {
                relationships: {
                    goods: {
                        type: "strongo-transport-order-goods",
                    },
                    features: {
                        type: "order-entry-features",
                    },
                }
            }
        },
        {
            type: "strongo-transport-order-goods", schema: {
                relationships: {
                    adrs: {
                        type: "strongo-transport-order-good-adrs",
                    },
                }
            }
        },
        {type: "strongo-transport-order-good-adrs", schema: {}},
        {type: "order-entry-feature-strongo-transport-orders", schema: {}},
        {type: "strongo-shipment-tracking-urls", schema: {}},
        {type: "track-trace-strongo-shipments", schema: {
                relationships: {
                    apiStatus: {
                        type: "api-statuses",
                    },
                }
            }
        },

        {type: 'legal-holidays', schema: {}},
        {type: 'domestic-order-entry-general-settings', schema: {}},
        {type: 'international-order-entry-general-settings', schema: {}},
        {type: 'strongo-order-entry-general-settings', schema: {}},
        {type: 'shippers', schema: {}},
        {
            type: 'track-trace-companies', schema: {
                relationships: {
                    internationalRoadOrderEntry: {
                        type: "track-trace-international-road-order-entries",
                    },
                    domesticRoadOrderEntry: {
                        type: "track-trace-domestic-road-order-entries",
                    },
                }
            }
        },
        {type: 'track-trace-international-road-order-entries', schema: {}},
        {type: 'track-trace-domestic-road-order-entries', schema: {}},
        {
            type: 'site-notifications', schema: {
                relationships: {
                    notificationInteraction: {
                        type: 'notification-interactions'
                    },
                },
            }
        },
        {
            type: 'releases', schema: {
                relationships: {
                    notificationInteraction: {
                        type: 'notification-interactions'
                    },
                },
            }
        },
        {
            type: 'maintenances', schema: {
                relationships: {
                    notificationInteraction: {
                        type: 'notification-interactions'
                    },
                },
            }
        },
        {type: 'klg-users', schema: {}},
        {type: 'user-settings', schema: {}},
        {type: 'notification-interactions', schema: {}},
        {type: 'company-kpis', schema: {}},
        {type: 'company-annexes', schema: {}},
        {type: 'company-contract-addendums', schema: {}},
        {
            type: 'company-contracts', schema: {
                relationships: {
                    companyContractAddendums: {
                        type: 'company-contract-addendums'
                    },
                    activeCompanyContractAddendums: {
                        type: 'company-contract-addendums'
                    },
                    companyAnnexes: {
                        type: 'company-annexes'
                    },
                    activeCompanyAnnexes: {
                        type: 'company-annexes'
                    },
                },
            }
        },
        {type: 'order-datas', schema: {}},
        {
            type: 'strongo-shipments',
            schema: {
                relationships: {
                    strongoShipmentParcels: {
                        type: "strongo-shipment-parcels"
                    },
                    transportOrder: {
                        type: "strongo-transport-orders"
                    }
                }
            }
        },
        {
            type: 'strongo-shipment-parcels', schema: {
                schema: {
                    relationships: {
                        strongoShipmentParcelHistories: {
                            type: "strongo_shipment_parcel_histories"
                        },
                        strongoShipmentRelatedAwb: {
                            type: "strongo_shipment_parcels"
                        },
                        strongoInvoice: {
                            type: "strongo_invoices"
                        },
                        transportOrder: {
                            type: "strongo-transport-orders"
                        },
                    }
                }
            }
        },
        {type: 'strongo-shipment-parcel-histories', schema: {}},
        {type: 'strongo-shipment-parcel-delivery-date-changes', schema: {
                relationships: {
                    strongoShipmentParcel: {
                        type: "strongo-shipment-parcels"
                    },
                }
            }},
        {type: 'strongo-invoices', schema: {
                relationships: {
                    strongoShipment: {
                        type: "strongo-shipments"
                    },
                    customerLedgerEntry: {
                        type: "customer_ledger_entry"
                    },
                }
            }},
        {type: 'strongo-departments', schema: {}},
        {type: 'dashboard-order-charts', schema: {}},
        {
            type: 'self-service-tokens', schema: {
                relationships: {
                    user: {
                        type: "users"
                    },
                }
            }
        },
        {
            type: 'customer-ledger-entry-reversals', schema: {
                relationships: {
                    reversalInvoice: {
                        type: "customer-ledger-entries"
                    },
                    originalInvoice: {
                        type: "customer-ledger-entries"
                    },
                }
            }
        },

    ];

    json_api_types.forEach(typeData => {
        Serializer.register(typeData.type, {
            ...typeData.schema,
            afterDeserialize: (data: any) => {
                data.id = parseInt(data.id);
                return data;
            }
        } as Options);
    });

    const jsonApiSerialize = <T>(type: string, data: T): JSONAPIDocument => {
        return Serializer.serialize(type, data);
    };

    const jsonApiDeserialize = <T>(type: string, data: JSONAPIDocument): T => {
        return Serializer.deserialize(type, data);
    };

    const jsonApiSerializeRelationToOne = <T>(type: string, data: Object): JSONAPIDocument => {
        return {
            data: {
                type: type,
                id: data.id.toString()
            }
        };
    };

    const jsonApiSerializeRelationToMany = <T>(type: string, data: Array<Object>): JSONAPIDocument => {
        const relationData = [];
        data.forEach(el => {
            relationData.push({
                type: type,
                id: el.id.toString()
            });
        });
        return {
            data: relationData
        };
    };

    function getItems<T, DT>(
        schema: string,
        type: { new(data: DT): T },
        relations: string | string[] | undefined = undefined,
        fields: { [key: string]: Array<string> } | undefined = undefined,
        filters: Array<QueryFilter> | undefined = undefined,
        pagination: JsonApiPagination | undefined = undefined,
        sort: string | string[] | undefined = undefined,
        cancelToken: CancelTokenSource | undefined = undefined,
        options: ApiCallOptions | undefined = undefined
    ): Promise<T[]> {
        return new Promise((resolve, reject) => {
            let jsonApiUrl = `/${schema}`;
            let queryParams: Array<string> = [];

            if (relations !== undefined) {
                if (typeof relations === "string") {
                    queryParams.push(`include=${relations}`);
                } else if (Array.isArray(relations)) {
                    queryParams.push(`include=${relations.join(",")}`);
                }
            }
            if (fields !== undefined) {
                Object.keys(fields).forEach(relationName => {
                    queryParams.push(`fields[${relationName}]=${fields[relationName].join(",")}`);
                });
            }

            if (Array.isArray(filters)) {
                filters.forEach(filter => {
                    if (filter.delimiter === null || filter.delimiter === undefined) {
                        filter.values.forEach(filter_value => {
                            if (filter.parent !== undefined) {
                                if (Array.isArray(filter.parent)) {
                                    let parents = '';
                                    filter.parent.forEach(parent => {
                                        parents += `[${parent}]`;
                                    })

                                    queryParams.push(`filter${parents}[${filter.field}]=${encodeURIComponent(filter_value)}`)
                                } else {
                                    queryParams.push(`filter[${filter.parent}][${filter.field}]=${encodeURIComponent(filter_value)}`)
                                }
                            } else {
                                queryParams.push(`filter[${filter.field}]=${encodeURIComponent(filter_value)}`)
                            }
                        })
                    } else {
                        if (filter.parent !== undefined) {
                            if (Array.isArray(filter.parent)) {
                                let parents = '';
                                filter.parent.forEach(parent => {
                                    parents += `[${parent}]`;
                                })

                                queryParams.push(`filter${parents}[${filter.field}]=${encodeURIComponent(filter.values.join(filter.delimiter))}`)
                            } else {
                                queryParams.push(`filter[${filter.parent}][${filter.field}]=${encodeURIComponent(filter.values.join(filter.delimiter))}`)
                            }
                        } else {
                            queryParams.push(`filter[${filter.field}]=${encodeURIComponent(filter.values.join(filter.delimiter))}`)
                        }
                    }
                })
            }

            if (pagination !== undefined) {
                queryParams.push(`page[number]=${pagination.page_number}`);
                queryParams.push(`page[size]=${pagination.page_size}`);
            }

            if (sort !== undefined) {
                if (typeof sort === "string") {
                    queryParams.push(`sort=${sort}`);
                } else if (Array.isArray(sort)) {
                    queryParams.push(`sort=${sort.join(",")}`);
                }
            }

            if (queryParams.length > 0) {
                jsonApiUrl += `?${queryParams.join("&")}`;
            }

            axiosApi.value.get(jsonApiUrl, {
                cancelToken: cancelToken?.token
            })
                .then((response) => {
                    const data_items: DT[] = jsonApiDeserialize<DT[]>(schema, response.data);
                    const items: T[] = [];
                    data_items.forEach(el => items.push(new type(el)));
                    if (response.data.meta) {
                        meta.value = response.data.meta;
                    } else {
                        meta.value = undefined;
                    }
                    resolve(items);
                })
                .catch((error) => {
                    if (error?.code === "ERR_CANCELED") {
                        reject("Call cancelled");
                        return;
                    }
                    let message = `${schema} error - `;
                    if (error.response?.data?.errors?.length > 0) {
                        error.response.data.errors.forEach(err => {
                            message += err.detail + "<br>";
                        });
                    }
                    console.log(error)
                    if (options === undefined || options.ignore_errors === undefined || options.ignore_errors === false) {
                        showError(`Get items failed`, message)
                    }
                    reject("An error has occured");
                });
        });
    }

    function getItem<T, DT>(schema: string, id: number, type: {
        new(data: DT): T
    }, relations: string | string[] | undefined = undefined): Promise<T> {
        return new Promise((resolve, reject) => {
            let jsonApiUrl = `/${schema}/${id}`;
            if (relations !== undefined) {
                if (typeof relations === "string") {
                    jsonApiUrl += `?include=${relations}`;
                } else if (Array.isArray(relations)) {
                    jsonApiUrl += `?include=${relations.join(",")}`;
                }
            }
            axiosApi.value.get(jsonApiUrl)
                .then((response) => {
                    const data_item: DT = jsonApiDeserialize<DT>(schema, response.data as JSONAPIDocument);
                    const item: T = new type(data_item);
                    resolve(item);
                })
                .catch((error) => {
                    if (error?.code === "ERR_CANCELED") {
                        reject("Call cancelled");
                        return;
                    }
                    let message = `${schema} error - `;
                    if (error.response?.data?.errors?.length > 0) {
                        error.response.data.errors.forEach(err => {
                            message += err.detail + "<br>"
                        })
                    }
                    console.log(error)
                    showError(`Get item failed`, message)
                    reject("An error has occured");
                });
        });
    }

    function createItem<T, DT>(schema: string, data: T, type: {
        new(data: DT): T
    }, extra: null | any = null, relations: string | string[] | undefined = undefined): Promise<T>
    {
        let extra_headers = {};
        if (extra) extra_headers = extra;

        return new Promise((resolve, reject) => {
            const payload: JSONAPIDocument = jsonApiSerialize<T>(schema, data);
            let jsonApiUrl = `/${schema}`;
            if (relations !== undefined) {
                if (typeof relations === "string") {
                    jsonApiUrl += `?include=${relations}`;
                } else if (Array.isArray(relations)) {
                    jsonApiUrl += `?include=${relations.join(",")}`;
                }
            }
            axiosApi.value.post(jsonApiUrl, payload, {
                headers: extra_headers
            })
                .then((response) => {
                    const data: DT = jsonApiDeserialize<DT>(schema, response.data as JSONAPIDocument);
                    const item: T = new type(data);
                    resolve(item);
                })
                .catch((error) => {
                    if (error?.code === "ERR_CANCELED") {
                        reject("Call cancelled");
                        return;
                    }
                    let message = `${schema} error - `;
                    if (error.response?.data?.errors?.length > 0) {
                        error.response.data.errors.forEach(err => {
                            message += err.detail + "<br>"
                        })
                    }
                    console.log(error)
                    showError(`Create item failed`, message)
                    reject("An error has occured");
                })
        });
    }

    function saveItem<T, DT>(schema: string, id: number, data: T | any, type: { new(data: DT): T },
                             relations: string | string[] | undefined = undefined): Promise<T>
    {
        return new Promise((resolve, reject) => {
            const payload: JSONAPIDocument = jsonApiSerialize<T>(schema, data);
            let jsonApiUrl = `/${schema}/${id}`;
            if (relations !== undefined) {
                if (typeof relations === "string") {
                    jsonApiUrl += `?include=${relations}`;
                } else if (Array.isArray(relations)) {
                    jsonApiUrl += `?include=${relations.join(",")}`;
                }
            }
            axiosApi.value.patch(jsonApiUrl, payload)
                .then((response) => {
                    const data: DT = jsonApiDeserialize<DT>(schema, response.data as JSONAPIDocument);
                    const item: T = new type(data);
                    if (response.data.meta) {
                        meta.value = response.data.meta;
                    } else {
                        meta.value = undefined;
                    }
                    resolve(item);
                })
                .catch((error) => {
                    if (error?.code === "ERR_CANCELED") {
                        reject("Call cancelled");
                        return;
                    }
                    let message = `${schema} error - `;
                    if (error.response?.data?.errors?.length > 0) {
                        error.response.data.errors.forEach(err => {
                            message += err.detail + "<br>"
                        })
                    }
                    console.log(error)
                    showError(`Update item failed`, message)
                    reject("An error has occured");
                })
        });
    }

    function deleteItem(schema: string, id: number): Promise<void> {
        return new Promise((resolve, reject) => {
            axiosApi.value.delete(`/${schema}/${id}`)
                .then((response) => {
                    resolve();
                })
                .catch((error) => {
                    if (error?.code === "ERR_CANCELED") {
                        reject("Call cancelled");
                        return;
                    }
                    let message = `${schema} error - `;
                    if (error.response?.data?.errors?.length > 0) {
                        error.response.data.errors.forEach(err => {
                            message += err.detail + "<br>"
                        })
                    }
                    console.log(error)
                    showError(`Delete item failed`, message)
                    reject("An error has occured");
                })
        });
    }

    function relationDeleteItem<T, DT>(schema: string, relationship: string, parent_id: number, related_id: number): Promise<void> {
        return new Promise((resolve, reject) => {
            const payload = {
                data: [
                    {
                        type: relationship,
                        id: related_id.toString()
                    }
                ]
            };
            console.log("delete", payload)
            let jsonApiUrl = `/${schema}/${parent_id}/relationships/${relationship}`;
            axiosApi.value.delete(jsonApiUrl, {data: payload})
                .then((response) => {
                    console.log("iii", response)
                    resolve();
                })
                .catch((error) => {
                    if (error?.code === "ERR_CANCELED") {
                        reject("Call cancelled");
                        return;
                    }
                    let message = `${schema} error - `;
                    if (error.response?.data?.errors?.length > 0) {
                        error.response.data.errors.forEach(err => {
                            message += err.detail + "<br>"
                        })
                    }
                    console.log(error)
                    showError(`Relation delete failed`, message)
                    reject("An error has occured");
                })
        });
    }

    function relationAddItem<T, DT>(schema: string, relationship: string, parent_id: number, data: any, type: {
        new(data: DT): T
    }): Promise<T> {
        return new Promise((resolve, reject) => {
            const payload: JSONAPIDocument = jsonApiSerializeRelationToOne<T>(relationship, data);
            let jsonApiUrl = `/${schema}/${parent_id}/relationships/${relationship}`;
            axiosApi.value.post(jsonApiUrl, payload)
                .then((response) => {
                    const data: DT = jsonApiDeserialize<DT>(schema, response.data as JSONAPIDocument);
                    const item: T = new type(data);
                    resolve(item);
                })
                .catch((error) => {
                    if (error?.code === "ERR_CANCELED") {
                        reject("Call cancelled");
                        return;
                    }
                    let message = `${schema} error - `;
                    if (error.response?.data?.errors?.length > 0) {
                        error.response.data.errors.forEach(err => {
                            message += err.detail + "<br>"
                        })
                    }
                    console.log(error)
                    showError(`Relation add item failed`, message)
                    reject("An error has occured");
                })
        });
    }

    function relationAddItems<T, DT>(schema: string, relationship: string, parent_id: number, data: any, type: {
        new(data: DT): T
    }): Promise<T> {
        return new Promise((resolve, reject) => {
            const payload: JSONAPIDocument = jsonApiSerializeRelationToMany<T>(relationship, data);
            let jsonApiUrl = `/${schema}/${parent_id}/relationships/${relationship}`;
            axiosApi.value.post(jsonApiUrl, payload)
                .then((response) => {
                    const data: DT = jsonApiDeserialize<DT>(schema, response.data as JSONAPIDocument);
                    const item: T = new type(data);
                    resolve(item);
                })
                .catch((error) => {
                    if (error?.code === "ERR_CANCELED") {
                        reject("Call cancelled");
                        return;
                    }
                    let message = `${schema} error - `;
                    if (error.response?.data?.errors?.length > 0) {
                        error.response.data.errors.forEach(err => {
                            message += err.detail + "<br>"
                        })
                    }
                    console.log(error)
                    showError(`Relation add items failed`, message)
                    reject("An error has occured");
                })
        });
    }

    function relationReplaceItem<T, DT>(schema: string, relationship: string, parent_id: number, data: any, type: {
        new(data: DT): T
    }): Promise<T> {
        return new Promise((resolve, reject) => {
            const payload: JSONAPIDocument = jsonApiSerializeRelationToOne<T>(relationship, data);
            let jsonApiUrl = `/${schema}/${parent_id}/relationships/${relationship}`;
            axiosApi.value.patch(jsonApiUrl, payload)
                .then((response) => {
                    const data: DT = jsonApiDeserialize<DT>(schema, response.data as JSONAPIDocument);
                    const item: T = new type(data);
                    resolve(item);
                })
                .catch((error) => {
                    if (error?.code === "ERR_CANCELED") {
                        reject("Call cancelled");
                        return;
                    }
                    let message = `${schema} error - `;
                    if (error.response?.data?.errors?.length > 0) {
                        error.response.data.errors.forEach(err => {
                            message += err.detail + "<br>"
                        })
                    }
                    console.log(error)
                    showError(`Relation replace failed`, message)
                    reject("An error has occured");
                })
        });
    }

    function relationReplaceItems<T, DT>(schema: string, relationship: string, parent_id: number, data: any, type: {
        new(data: DT): T
    }): Promise<T> {
        return new Promise((resolve, reject) => {
            const payload: JSONAPIDocument = jsonApiSerializeRelationToMany<T>(relationship, data);
            let jsonApiUrl = `/${schema}/${parent_id}/relationships/${relationship}`;
            axiosApi.value.patch(jsonApiUrl, payload)
                .then((response) => {
                    const data: DT = jsonApiDeserialize<DT>(schema, response.data as JSONAPIDocument);
                    const item: T = new type(data);
                    resolve(item);
                })
                .catch((error) => {
                    if (error?.code === "ERR_CANCELED") {
                        reject("Call cancelled");
                        return;
                    }
                    let message = `${schema} error - `;
                    if (error.response?.data?.errors?.length > 0) {
                        error.response.data.errors.forEach(err => {
                            message += err.detail + "<br>"
                        })
                    }
                    console.log(error)
                    showError(`Relation replace items failed`, message)
                    reject("An error has occured");
                })
        });
    }

    function relationGetItem<T, DT>(schema: string, relationship: string, parent_id: number, type: {
        new(data: DT): T
    }): Promise<T> {
        return new Promise((resolve, reject) => {
            let jsonApiUrl = `/${schema}/${parent_id}/relationships/${relationship}`;
            axiosApi.value.get(jsonApiUrl)
                .then((response) => {
                    const data_item: DT = jsonApiDeserialize<DT>(schema, response.data as JSONAPIDocument);
                    const item: T = new type(data_item);
                    resolve(item);
                })
                .catch((error) => {
                    if (error?.code === "ERR_CANCELED") {
                        reject("Call cancelled");
                        return;
                    }
                    let message = `${schema} error - `;
                    if (error.response?.data?.errors?.length > 0) {
                        error.response.data.errors.forEach(err => {
                            message += err.detail + "<br>"
                        })
                    }
                    console.log(error)
                    showError(`Relation get item failed`, message)
                    reject("An error has occured");
                })
        });
    }

    function relationGetItems<T, DT>(schema: string,
                                     relationship: string,
                                     parent_id: number,
                                     type: { new(data: DT): T },
                                     relations: string | string[] | undefined = undefined,
                                     fields: { [key: string]: Array<string> } | undefined = undefined,
                                     filters: Array<QueryFilter> | undefined = undefined,
                                     pagination: JsonApiPagination | undefined = undefined,
                                     sort: string | string[] | undefined = undefined,
    ): Promise<T[]> {
        return new Promise((resolve, reject) => {
            let jsonApiUrl = `/${schema}/${parent_id}/relationships/${relationship}`;
            let queryParams: Array<string> = [];

            if (relations !== undefined) {
                if (typeof relations === 'string') {
                    queryParams.push(`include=${relations}`);
                } else if (Array.isArray(relations)) {
                    queryParams.push(`include=${relations.join(',')}`);
                }
            }
            if (fields !== undefined) {
                Object.keys(fields).forEach(relationName => {
                    queryParams.push(`fields[${relationName}]=${fields[relationName].join(',')}`);
                })
            }

            if (Array.isArray(filters)) {
                filters.forEach(filter => {
                    if (filter.delimiter === null || filter.delimiter === undefined) {
                        filter.values.forEach(filter_value => {
                            if (filter.parent !== undefined) {
                                if (Array.isArray(filter.parent)) {
                                    let parents = '';
                                    filter.parent.forEach(parent => {
                                        parents += `[${parent}]`;
                                    })

                                    queryParams.push(`filter${parents}[${filter.field}]=${encodeURIComponent(filter_value)}`)
                                } else {
                                    queryParams.push(`filter[${filter.parent}][${filter.field}]=${encodeURIComponent(filter_value)}`)
                                }
                            } else {
                                queryParams.push(`filter[${filter.field}]=${encodeURIComponent(filter_value)}`)
                            }
                        })
                    } else {
                        if (filter.parent !== undefined) {
                            if (Array.isArray(filter.parent)) {
                                let parents = '';
                                filter.parent.forEach(parent => {
                                    parents += `[${parent}]`;
                                })

                                queryParams.push(`filter${parents}[${filter.field}]=${encodeURIComponent(filter.values.join(filter.delimiter))}`)
                            } else {
                                queryParams.push(`filter[${filter.parent}][${filter.field}]=${encodeURIComponent(filter.values.join(filter.delimiter))}`)
                            }
                        } else {
                            queryParams.push(`filter[${filter.field}]=${encodeURIComponent(filter.values.join(filter.delimiter))}`)
                        }
                    }
                })
            }

            if (pagination !== undefined) {
                queryParams.push(`page[number]=${pagination.page_number}`);
                queryParams.push(`page[size]=${pagination.page_size}`);
            }

            if (sort !== undefined) {
                if (typeof sort === 'string') {
                    queryParams.push(`sort=${sort}`);
                } else if (Array.isArray(sort)) {
                    queryParams.push(`sort=${sort.join(',')}`);
                }
            }

            if (queryParams.length > 0) {
                jsonApiUrl += `?${queryParams.join('&')}`;
            }

            axiosApi.value.get(jsonApiUrl)
                .then((response) => {

                    const data_items: DT[] = jsonApiDeserialize<DT[]>(relationship, response.data as JSONAPIDocument);
                    const items: T[] = [];
                    // console.log("aaaa1", response)
                    // console.log("aaaa2", data_items)

                    if (relations !== undefined) {
                        response.data?.data?.forEach(el => {
                            const includedResource = response.data?.included?.find(includedEl =>
                                el.id === includedEl.id &&
                                el.type === includedEl.type
                            );

                            if (includedResource !== undefined) {
                                const newItem = new type(includedResource.attributes);
                                newItem.id = parseInt(includedResource.id);
                                items.push(newItem);
                            }
                        })
                        resolve(items);
                    } else {
                        resolve(data_items);
                    }

                    if (response.data.meta) {
                        meta.value = response.data.meta;
                    } else {
                        meta.value = undefined;
                    }
                })
                .catch((error) => {
                    if (error?.code === "ERR_CANCELED") {
                        reject("Call cancelled");
                        return;
                    }
                    let message = `${schema} error - `;
                    if (error.response?.data?.errors?.length > 0) {
                        error.response.data.errors.forEach(err => {
                            message += err.detail + "<br>"
                        })
                    }
                    console.log(error)
                    showError(`Relation get items failed`, message)
                    reject("An error has occured");
                })
        });
    }

    function getCustomAction<T, DT>(
        schema: string,
        parent_id: number | null,
        action: string,
        type: {
            new(data: DT): T | undefined
        } = undefined,
        params: Array<{ name: string, value: string }> | undefined = undefined,
        filters: Array<QueryFilter> | undefined = undefined,
        cancelToken: CancelTokenSource | undefined = undefined
    ): Promise<T | any> {
        return new Promise((resolve, reject): any => {
            let jsonApiUrl = `/${schema}/`;
            let queryParams: Array<string> = [];

            if (parent_id !== null) {
                jsonApiUrl += `${parent_id}/`;
            }

            jsonApiUrl += `-actions/${action}`;

            if (Array.isArray(params)) {
                params.forEach(param => {
                    queryParams.push(`${param.name}=${param.value}`)
                })
            }

            if (Array.isArray(filters)) {
                filters.forEach(filter => {
                    if (filter.delimiter === null || filter.delimiter === undefined) {
                        filter.values.forEach(filter_value => {
                            if (filter.parent !== undefined) {
                                if (Array.isArray(filter.parent)) {
                                    let parents = '';
                                    filter.parent.forEach(parent => {
                                        parents += `[${parent}]`;
                                    })

                                    queryParams.push(`filter${parents}[${filter.field}]=${encodeURIComponent(filter_value)}`)
                                } else {
                                    queryParams.push(`filter[${filter.parent}][${filter.field}]=${encodeURIComponent(filter_value)}`)
                                }
                            } else {
                                queryParams.push(`filter[${filter.field}]=${encodeURIComponent(filter_value)}`)
                            }
                        })
                    } else {
                        if (filter.parent !== undefined) {
                            if (Array.isArray(filter.parent)) {
                                let parents = '';
                                filter.parent.forEach(parent => {
                                    parents += `[${parent}]`;
                                })

                                queryParams.push(`filter${parents}[${filter.field}]=${encodeURIComponent(filter.values.join(filter.delimiter))}`)
                            } else {
                                queryParams.push(`filter[${filter.parent}][${filter.field}]=${encodeURIComponent(filter.values.join(filter.delimiter))}`)
                            }
                        } else {
                            queryParams.push(`filter[${filter.field}]=${encodeURIComponent(filter.values.join(filter.delimiter))}`)
                        }
                    }
                })
            }

            if (queryParams.length > 0) {
                jsonApiUrl += `?${queryParams.join('&')}`;
            }

            axiosApi.value.get(jsonApiUrl, {
                cancelToken: cancelToken?.token
            })
                .then((response) => {
                    if (type !== undefined) {
                        const data: DT = jsonApiDeserialize<DT>(schema, response.data as JSONAPIDocument);
                        const item: T = new type(data);
                        resolve(item);
                    } else {
                        resolve(response.data);
                    }
                })
                .catch((error) => {
                    if (error?.code === "ERR_CANCELED") {
                        reject("Call cancelled");
                        return;
                    }
                    let title = `Get custom action failed`;
                    let message = `${schema}/${action} error - `;
                    if (error.response?.data?.errors?.length > 0) {
                        title = error.response.data.errors[0].title ? error.response.data.errors[0].title : title;
                        error.response.data.errors.forEach(err => {
                            message += err.detail + "<br>"
                        })
                    }
                    console.log(error)
                    if (authStore.isLoggedIn) {
                        showError(title, message)
                    }
                    reject("An error has occured");
                })
        });
    }

    function postCustomAction<T, DT>(schema: string, parent_id: number | null, action: string, payload: any, type: {
        new(data: DT): T | undefined
    } = undefined): Promise<T | any> {
        return new Promise((resolve, reject): any => {

            let jsonApiUrl = `/${schema}/`;
            if (parent_id !== null) {
                jsonApiUrl += `${parent_id}/`;
            }

            jsonApiUrl += `-actions/${action}`;

            const call_data: JSONAPIDocument = jsonApiSerialize(schema, payload);

            axiosApi.value.post(jsonApiUrl, call_data)
                .then((response) => {
                    if (type !== undefined) {
                        const data: DT = jsonApiDeserialize<DT>(schema, response.data as JSONAPIDocument);
                        const item: T = new type(data);
                        resolve(item);
                    } else {
                        resolve(response.data);
                    }
                })
                .catch((error) => {
                    if (error?.code === "ERR_CANCELED") {
                        reject("Call cancelled");
                        return;
                    }
                    let message = `${schema}/${action} error\n` + "<br><br>";
                    if (error.response?.data?.errors?.length > 0) {
                        error.response.data.errors.forEach(err => {
                            message += err.detail + "<br>"
                        })
                    }
                    console.log(error)
                    showError(`Post custom action failed`, message)
                    reject("An error has occured");
                })
        });
    }

    return {
        BASE_URL,
        PARALLEL_REQUESTS,
        axiosApi,
        axiosAuth,
        jsonApiSerialize,
        jsonApiDeserialize,
        meta,
        getItems,
        getItem,
        createItem,
        saveItem,
        relationDeleteItem,
        deleteItem,
        relationAddItem,
        relationAddItems,
        relationReplaceItem,
        relationReplaceItems,
        relationGetItem,
        relationGetItems,
        getCustomAction,
        postCustomAction,
        CancelToken
    };
});
